import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Button, cn, Select, SelectItem, SelectSection, SharedSelection } from "@nextui-org/react";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Breakpoint, useBreakpoint } from "cenero-ui-library";
import BookingsListCalendar from "@/components/bookings/calendar/BookingsListCalendar";
import MenuSide from "@/components/menu/MenuSide";
import PageHeader from "@/components/shared/PageHeader";
import { useBookingsCalendarStore } from "@/store/BookingsCalendarStore";
import { useRouteTracker } from "@/hooks/useRouteTracker";
import { getFormattedDate } from "@/lib/helpers";

// ---------------------- Component

const CalendarPage = () => {
  // --------- Variables / Hooks

  const breakpoint = useBreakpoint();

  const location = useLocation();

  useRouteTracker();

  const { setAllDaysExpanded, setNoDaysExpanded, selectedFilter, setSelectedFilter } =
    useBookingsCalendarStore();

  const isMobile = breakpoint < Breakpoint.md;

  const isDesktop = breakpoint >= Breakpoint.xl;

  const isBookingSelected =
    location.pathname.includes("/details/") || location.pathname.includes("/checkout/");

  const quickFilter = {
    general: [{ key: "all", label: "All" }],
    weeks: [
      { key: "last-week", label: "Last Week" },
      { key: "this-week", label: "This Week" },
      { key: "next-week", label: "Next Week" },
    ],
    months: [
      { key: "last-month", label: "Last Month" },
      { key: "this-month", label: "This Month" },
      { key: "next-month", label: "Next Month" },
    ],
  };

  // --------- Helpers

  const handleSelectionChange = (keys: SharedSelection) => {
    const selected = typeof keys === "string" ? keys : keys.currentKey;

    if (selected) {
      setSelectedFilter(selected);
    }
  };

  // --------- Effects

  useEffect(() => {
    setNoDaysExpanded();
  }, []);

  // --------- JSX

  return (
    <>
      <div className="flex h-dvh w-screen flex-col items-center justify-start overflow-hidden bg-gray-50 p-3 dark:bg-gray-950">
        {/*******************************************/}
        {/* Header */}
        {/*******************************************/}

        <PageHeader />

        <main
          className={cn(
            "flex max-h-dvh w-screen max-w-[768px] flex-1 flex-row gap-4 overflow-hidden px-2 xs:px-8 sm:px-10 md:max-w-none md:py-6",
            [!isMobile && "md:p-2 md:px-5"],
            [isDesktop && "max-w-[1600px]"],
          )}
        >
          {/*******************************************/}
          {/* Side Menu */}
          {/*******************************************/}

          {!isMobile && <MenuSide />}

          <div className={cn("flex w-full flex-1 gap-4", isDesktop ? "flex-row" : "flex-col")}>
            <div
              className={cn(
                "standard-shadow max-h-dvh flex-1 flex-col overflow-hidden rounded-xl bg-white p-1 dark:bg-gray-900",
                isDesktop && "w-1/2 flex-none",
              )}
            >
              <div className="size-full overflow-y-auto overflow-x-hidden rounded-xl border border-gray-50 p-2 dark:border-gray-950 xs:p-4 sm:p-6 md:p-8">
                <div className="flex h-auto w-full flex-col items-center justify-center">
                  <div className="w-full max-w-[500px]">
                    <div className="my-0 flex w-full flex-col items-start md:my-0">
                      {/*******************************************/}
                      {/* Page Title */}
                      {/*******************************************/}

                      <h1 className="mb-1 text-xl font-semibold text-gray-800 dark:text-gray-200 sm:text-2xl">
                        Calendar
                      </h1>

                      {/*******************************************/}
                      {/* Date */}
                      {/*******************************************/}

                      <h2 className="font-base text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                        Today is:
                        <span className="ml-1 text-blue-500 dark:text-blue-300">
                          {getFormattedDate()}
                        </span>
                      </h2>
                    </div>

                    <div className="-mr-2 mb-3 mt-6 flex w-auto flex-row items-center justify-between gap-2 sm:gap-3">
                      {/*******************************************/}
                      {/* Filters */}
                      {/*******************************************/}

                      <Select
                        size="sm"
                        variant="bordered"
                        label="Quick Filter"
                        placeholder="Select a date range to show"
                        className="max-w-56 rounded-[.875rem]"
                        classNames={{
                          value: "fix-autocomplete font-medium !text-gray-700 dark:!text-gray-100",
                          label: "font-semibold text-sm !text-gray-900 dark:!text-gray-200",
                        }}
                        selectedKeys={new Set([selectedFilter])}
                        onSelectionChange={handleSelectionChange}
                        selectionMode="single"
                      >
                        <SelectSection>
                          {quickFilter.general.map((filter) => (
                            <SelectItem
                              key={filter.key}
                              value={filter.key}
                            >
                              {filter.label}
                            </SelectItem>
                          ))}
                        </SelectSection>

                        <SelectSection title="Weeks">
                          {quickFilter.weeks.map((filter) => (
                            <SelectItem
                              key={filter.key}
                              value={filter.key}
                            >
                              {filter.label}
                            </SelectItem>
                          ))}
                        </SelectSection>

                        <SelectSection title="Months">
                          {quickFilter.months.map((filter) => (
                            <SelectItem
                              key={filter.key}
                              value={filter.key}
                            >
                              {filter.label}
                            </SelectItem>
                          ))}
                        </SelectSection>
                      </Select>

                      <div className="flex w-auto flex-row items-center justify-end gap-2 sm:gap-3">
                        {/*******************************************/}
                        {/* Collapse All */}
                        {/*******************************************/}

                        <Button
                          isIconOnly
                          radius="full"
                          className="bg-transparent p-2 text-current transition-all duration-200"
                          onPress={setNoDaysExpanded}
                        >
                          <FontAwesomeIcon
                            icon={faChevronUp}
                            className="h-4 text-gray-600 dark:text-gray-400 sm:h-5"
                          />
                        </Button>

                        {/*******************************************/}
                        {/* Expand All */}
                        {/*******************************************/}

                        <Button
                          isIconOnly
                          radius="full"
                          className="bg-transparent p-2 text-current transition-all duration-200"
                          onPress={setAllDaysExpanded}
                        >
                          <FontAwesomeIcon
                            icon={faChevronDown}
                            className="h-4 text-gray-600 dark:text-gray-400 sm:h-5"
                          />
                        </Button>
                      </div>
                    </div>

                    {/*******************************************/}
                    {/* Bookings List */}
                    {/*******************************************/}

                    <BookingsListCalendar />
                  </div>
                </div>
              </div>
            </div>

            {/*******************************************/}
            {/* Modal Area (if desktop) */}
            {/*******************************************/}

            {isDesktop && (
              <div className="standard-shadow max-h-dvh flex-1 flex-col overflow-hidden rounded-xl bg-white p-1 dark:bg-gray-900">
                <div className="size-full overflow-y-auto overflow-x-hidden rounded-xl border border-gray-50 p-2 dark:border-gray-950 xs:p-4 sm:p-6 md:p-8">
                  {isBookingSelected ? (
                    <Outlet />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <p className="text-center text-gray-500 dark:text-gray-400">
                        Selected Booking Information Will Appear Here
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/*******************************************/}
      {/* Modal Area (if mobile) */}
      {/*******************************************/}

      {!isDesktop && <Outlet />}
    </>
  );
};

export default CalendarPage;
